import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  BaseSite,
  BaseSiteService,
  ConfigInitializer,
  JavaRegExpConverter,
  SiteContextConfig,
  WindowRef,
} from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  BASE_SITE_CONTEXT_ID,
  COUNTRY_CONTEXT_ID,
  CURRENCY_CONTEXT_ID,
  LANGUAGE_CONTEXT_ID,
  THEME_CONTEXT_ID,
} from './context-ids';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class SiteContextConfigInitializer implements ConfigInitializer {
  readonly scopes = ['context'];
  readonly configFactory = () => this.resolveConfig().toPromise();
  platformId = inject(PLATFORM_ID);
  constructor(
    protected baseSiteService: BaseSiteService,
    protected javaRegExpConverter: JavaRegExpConverter,
    protected winRef: WindowRef
  ) { }

  private get currentUrl(): string {
    return this.winRef.location.href as string;
  }

  protected resolveConfig(): Observable<SiteContextConfig> {
    return this.baseSiteService.getAll().pipe(
      map((baseSites) =>
        baseSites?.find((site) => this.isCurrentBaseSite(site))
      ),
      filter((baseSite: any) => {
        if (!baseSite) {
          throw new Error(
            `Error: Cannot get base site config! Current url (${this.currentUrl}) doesn't match any of url patterns of any base sites.`
          );
        }
        return Boolean(baseSite);
      }),
      map((baseSite) => this.getConfig(baseSite)),
      take(1)
    );
  }

  protected getConfig(source: BaseSite): SiteContextConfig {
    if (isPlatformBrowser(this.platformId)) {
      const result = {
        context: {
          urlParameters: this.getUrlParams(source.urlEncodingAttributes),
          [BASE_SITE_CONTEXT_ID]: [source.uid],
          [LANGUAGE_CONTEXT_ID]: this.getIsoCodes(
            source.baseStore?.languages,
            source.defaultLanguage || source.baseStore?.defaultLanguage
          ),
          [CURRENCY_CONTEXT_ID]: this.getIsoCodes(
            source.baseStore?.currencies,
            source.baseStore?.defaultCurrency
          ),
          [THEME_CONTEXT_ID]: [source.theme],
          [COUNTRY_CONTEXT_ID]: [localStorage.getItem('country')],
        },
      } as SiteContextConfig;
      return result;
    }
    else {
      return null
    }
  }

  private isCurrentBaseSite(site: BaseSite): boolean {
    const index = (site.urlPatterns || []).findIndex((javaRegexp: string) => {
      const jsRegexp = this.javaRegExpConverter.toJsRegExp(javaRegexp);
      if (jsRegexp) {
        const result = jsRegexp.test(this.currentUrl);
        return result;
      } else {
        return false;
      }
    });
    return index !== -1;
  }

  private getUrlParams(params: string[] | undefined): string[] {
    const STOREFRONT_PARAM = 'storefront';
    return (params || []).map((param) =>
      param === STOREFRONT_PARAM ? BASE_SITE_CONTEXT_ID : param
    );
  }

  private getIsoCodes(
    elements: { isocode?: string }[] | undefined,
    defaultElement: { isocode?: string } | undefined
  ) {
    if (elements && defaultElement) {
      const result = this.moveToFirst(
        elements,
        (el) => el.isocode === defaultElement.isocode
      ).map((el) => el.isocode);
      return result;
    }
    return [];
  }

  private moveToFirst(array: any[], predicate: (el: any) => boolean): any[] {
    array = [...array];
    const index = array.findIndex(predicate);
    if (index !== -1) {
      const [el] = array.splice(index, 1);
      array.unshift(el);
    }
    return array;
  }
}
